import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@app/router/routes';
import HeroBanner from '@shared/components/HeroBanner';
import { getLampRegionList, getLightingPostList } from '@features/lamp-lighting/api/lampService';
import { useLanguage } from '@shared/contexts/LanguageContext';

const BreathingLamp = lazy(() =>
    import('../components/ThreeBreathingLamp/ThreeBreathingLamp')
);

/**
 * 精舍報導列表頁面 - 對應 code 5.html
 * Branch Activity Reports Page
 *
 * 注意：此頁面不需要包裝 PageLayout，因為 router 層級的 PageLayoutWrapper 已經提供。
 */

// 為每個地區分配不同的背景色
const REGION_BG_COLORS = [
    '#E8DA9A', '#E9D79A', '#E9D39A', '#EBCE9A', '#EBC99A',
    '#ECC599', '#EDC099', '#EEBC98', '#EEB898', '#E8DA9A',
    '#E9D39A', '#EBCE9A', '#ECC599', '#EDC099', '#EEBC98',
    '#EEB898', '#E8DA9A',
];

const BranchReportsPage = () => {
    const navigate = useNavigate();
    const { t, currentLanguage } = useLanguage();
    const [regions, setRegions] = useState([]);
    const [featuredItems, setFeaturedItems] = useState([]);
    const [expandedMap, setExpandedMap] = useState({});
    const [expandingRegion, setExpandingRegion] = useState(null);

    useEffect(() => {
        getLampRegionList()
            .then((res) => {
                setRegions(res.items || []);
            })
            .catch((err) => {
                console.error('Failed to fetch region list:', err);
            });

        // 取得全部精舍報導，按 monastery_id 去重，只取名稱和圖片
        getLightingPostList({ per_page: 100 })
            .then((res) => {
                const items = res.items || [];
                const seen = new Set();
                const unique = [];
                for (const item of items) {
                    if (!seen.has(item.monastery_id)) {
                        seen.add(item.monastery_id);
                        unique.push({
                            id: item.monastery_id,
                            name: item.monastery_name,
                            image: item.image?.[0]?.cache_url || '',
                            like_count: item.like_count ?? 0,
                        });
                    }
                }
                setFeaturedItems(unique.slice(0, 6));
            })
            .catch((err) => {
                console.error('Failed to fetch featured posts:', err);
            });
    }, [currentLanguage]);

    const toggleRegion = useCallback((regionName) => {
        const wasOpen = expandedMap[regionName];

        if (wasOpen) {
            setExpandedMap((prev) => ({ ...prev, [regionName]: false }));
        } else {
            // 展開此地區，關閉其他
            setExpandedMap((prev) => {
                const next = {};
                Object.keys(prev).forEach(k => next[k] = false);
                next[regionName] = true;
                return next;
            });

            // 觸發光暈爆發效果
            setExpandingRegion(regionName);
            setTimeout(() => setExpandingRegion(null), 1000);
        }
    }, [expandedMap]);

    // 處理精舍點擊，導航到詳情頁
    const handleBranchClick = (monastery) => {
        if (monastery.has_lighting_post) {
            navigate(ROUTES.BRANCH_REPORT_DETAIL(monastery.id), {
                state: { monasteryId: monastery.id, monasteryName: monastery.name }
            });
        }
    };

    // 遞迴渲染精舍列表（處理 region -> sub_region -> monastery 的階層）
    const renderMonasteries = (items) => {
        if (!items || items.length === 0) return null;

        return items.map((item) => {
            // 如果是 monastery，直接顯示
            if (item.type === 'monastery') {
                return (
                    <div
                        key={item.id}
                        onClick={() => handleBranchClick(item)}
                        className={`px-4 py-3 rounded-lg transition-all ${item.has_lighting_post
                                ? 'cursor-pointer hover:bg-[var(--color-gold)]/10 hover:shadow-md'
                                : 'opacity-50 cursor-not-allowed'
                            }`}
                        style={{
                            backgroundColor: item.has_lighting_post ? 'white' : 'rgba(255,255,255,0.6)',
                        }}
                    >
                        <div className="flex items-center justify-between">
                            <span
                                className="text-base md:text-lg font-medium"
                                style={{ color: 'var(--color-text-main)' }}
                            >
                                {item.name}
                            </span>
                            {item.has_lighting_post && (
                                <span className="material-symbols-outlined text-[var(--color-gold)]">
                                    chevron_right
                                </span>
                            )}
                        </div>
                    </div>
                );
            }

            // 如果是 sub_region，顯示子區域標題並遞迴渲染子項
            if (item.type === 'sub_region') {
                return (
                    <div key={item.name} className="mb-4">
                        <h4
                            className="text-lg md:text-xl font-semibold mb-3 px-2"
                            style={{ color: 'var(--color-warning)' }}
                        >
                            {item.name}
                        </h4>
                        <div className="space-y-2">
                            {renderMonasteries(item.children)}
                        </div>
                    </div>
                );
            }

            return null;
        });
    };

    // 處理焦點精舍點擊
    const handleFeaturedClick = (item) => {
        navigate(ROUTES.BRANCH_REPORT_DETAIL(item.id), {
            state: { monasteryId: item.id, monasteryName: item.name }
        });
    };

    return (
        <>
            {/* Hero Banner */}
            <HeroBanner />

            {/* Featured Section - 本週焦點精舍 */}
            <section className="py-10 md:py-14 lg:py-20" style={{ backgroundColor: 'var(--color-background)' }}>
                <div className="max-w-6xl mx-auto">
                    {/* 標題區 */}
                    <div className="text-center mb-10 md:mb-12 px-6">
                        <p className="text-xs md:text-sm tracking-[0.3em] uppercase mb-3" style={{ color: 'var(--color-accent)' }}>
                            Featured This Week
                        </p>
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ color: 'var(--color-warning)' }}>
                            {t('branch-reports.featuredTitle')}
                        </h3>
                        <div className="flex items-center justify-center gap-3 mt-5">
                            <div className="w-8 md:w-12 h-px" style={{ backgroundColor: 'var(--color-accent)' }} />
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: 'var(--color-accent)' }} />
                            <div className="w-8 md:w-12 h-px" style={{ backgroundColor: 'var(--color-accent)' }} />
                        </div>
                    </div>

                    {/* 卡片區 */}
                    <div className="flex overflow-x-auto md:grid md:grid-cols-3 gap-5 md:gap-7 px-6 pb-4 md:pb-0 no-scrollbar">
                        {featuredItems.map((item) => (
                            <div
                                key={item.id}
                                onClick={() => handleFeaturedClick(item)}
                                className="flex-shrink-0 w-[200px] md:w-auto cursor-pointer group"
                            >
                                {/* 圖片容器 */}
                                <div
                                    className="relative overflow-hidden transition-all duration-300 group-hover:shadow-xl"
                                    style={{ borderRadius: '24px', boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)' }}
                                >
                                    <div className="aspect-square">
                                        {item.image ? (
                                            <img
                                                alt={item.name}
                                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                                src={item.image}
                                            />
                                        ) : (
                                            <div
                                                className="w-full h-full flex flex-col items-center justify-center gap-3"
                                                style={{ background: 'linear-gradient(135deg, var(--color-background) 0%, #EDE8DF 100%)' }}
                                            >
                                                <span className="material-symbols-outlined text-5xl md:text-6xl" style={{ color: 'var(--color-accent)', opacity: 0.5 }}>account_balance</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* 右上角 like badge */}
                                    {item.like_count > 0 && (
                                        <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-black/55 backdrop-blur-sm shadow-lg">
                                            <span className="material-symbols-outlined text-base text-red-400 leading-none" style={{ fontVariationSettings: "'FILL' 1" }}>favorite</span>
                                            <span className="text-sm text-white font-bold">{item.like_count}</span>
                                        </div>
                                    )}
                                    {/* 底部漸層名稱 overlay */}
                                    <div
                                        className="absolute bottom-0 left-0 right-0 px-4 pb-4 pt-12"
                                        style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)' }}
                                    >
                                        <h4 className="text-white text-base md:text-lg font-medium tracking-wider text-center drop-shadow-md">
                                            {item.name}
                                        </h4>
                                    </div>
                                </div>

                                {/* 底部查看提示 */}
                                <div className="flex items-center justify-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <span className="text-xs" style={{ color: 'var(--color-accent)' }}>{t('branch-reports.viewReport')}</span>
                                    <span className="material-symbols-outlined text-sm" style={{ color: 'var(--color-accent)' }}>arrow_forward</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Regions Section - 直式卡片列表 (從 API 取得) */}
            <section className="py-8 md:py-12 lg:py-16 px-4 md:px-6" style={{ backgroundColor: 'var(--color-background)' }}>
                <div className="max-w-6xl mx-auto">
                    <div className="text-center mb-8 md:mb-10">
                        <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold" style={{ color: 'var(--color-warning)' }}>
                            {t('branch-reports.sectionTitle')}
                        </h3>
                        <div className="w-16 md:w-20 h-1 mx-auto mt-4" style={{ backgroundColor: 'var(--color-accent)' }} />
                    </div>

                    <div className="flex flex-col gap-3">
                        {regions.map((region, regionIdx) => {
                            const isOpen = expandedMap[region.name];
                            const isThisExpanding = expandingRegion === region.name;
                            // 計算該地區的精舍總數（包含子區域）
                            const monasteryCount = region.children
                                ? region.children.reduce((count, child) => {
                                    if (child.type === 'monastery') return count + 1;
                                    if (child.type === 'sub_region') {
                                        return count + (child.children?.length || 0);
                                    }
                                    return count;
                                }, 0)
                                : 0;

                            return (
                                <div
                                    key={region.name}
                                    className="rounded-[20px] overflow-hidden"
                                    style={{
                                        backgroundColor: REGION_BG_COLORS[regionIdx % REGION_BG_COLORS.length],
                                        boxShadow: isOpen
                                            ? '0 2px 20px rgba(212, 175, 55, 0.3)'
                                            : '0 2px 12px rgba(0,0,0,0.1)',
                                        transition: 'box-shadow 0.5s ease',
                                    }}
                                >
                                    {/* 地區名稱 - 點擊折疊 */}
                                    <button
                                        type="button"
                                        className="w-full flex items-center justify-between px-3 md:px-5 py-3 md:py-4 text-left"
                                        onClick={() => toggleRegion(region.name)}
                                    >
                                        <div className="flex items-center gap-1">
                                            {/* 呼吸燈 */}
                                            <Suspense fallback={<div style={{ width: 52, height: 52 }} className="flex-shrink-0" />}>
                                                <BreathingLamp
                                                    regionName={region.name}
                                                    isActive={isOpen}
                                                    isExpanding={isThisExpanding}
                                                />
                                            </Suspense>
                                            <h4
                                                className="text-xl md:text-2xl font-bold"
                                                style={{ color: 'var(--color-text-main)' }}
                                            >
                                                {region.name}
                                            </h4>
                                        </div>
                                        <span className="flex items-center gap-3">
                                            <span className="text-sm" style={{ color: 'var(--color-text-sub)' }}>
                                                {t('branch-reports.monasteryCount').replace('{{count}}', monasteryCount)}
                                            </span>
                                            <span
                                                className="transition-transform duration-300"
                                                style={{
                                                    transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                                    color: 'var(--color-text-sub)',
                                                }}
                                            >
                                                ▼
                                            </span>
                                        </span>
                                    </button>

                                    {/* 精舍列表 - CSS 過渡動畫 */}
                                    <div
                                        className="overflow-hidden transition-all duration-500 ease-out"
                                        style={{
                                            maxHeight: isOpen ? '2000px' : '0px',
                                            opacity: isOpen ? 1 : 0,
                                        }}
                                    >
                                        <div
                                            className="mx-3 mb-3 rounded-[14px] overflow-hidden"
                                            style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
                                        >
                                            <div className="p-4 space-y-3">
                                                {renderMonasteries(region.children)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
        </>
    );
};


export default BranchReportsPage;
